import { Icons } from '@/app/components/ui/Icons';
import '@/app/styles/components/HotelsPagination.css';
import { useTranslations } from 'next-intl';
import React from 'react';

interface HotelsPaginationProps {
	totalPages: number;
	currentPage: number; // 1-based index
	totalResults: number;
	onPageClick: (page: number) => void;
	className?: string;
}

const clamp = (val: number, min: number, max: number) => Math.max(min, Math.min(max, val));

const buildPages = (totalPages: number, currentPage: number): (number | 'ellipsis')[] => {
	// Always show max 4 page numbers. Include first and last with ellipsis when needed.
	if (totalPages <= 4) {
		return Array.from({ length: totalPages }, (_, i) => i + 1);
	}

	// Ensure current within range
	const cur = clamp(currentPage, 1, totalPages);

	// Cases:
	// 1) Near start: 1 2 3 ... last
	if (cur <= 3) return [1, 2, 3, 4, 'ellipsis', totalPages];

	// 2) Near end: 1 ... last-2 last-1 last
	if (cur >= totalPages - 2) return [1, 'ellipsis', totalPages - 2, totalPages - 1, totalPages];

	// 3) Middle: 1 ... cur ... last (center current among four items -> show cur-1, cur, cur+1 which is 3; to keep max 4 we also include 1 and last with one ellipsis)
	// Keep exactly 4 numbers displayed by choosing: 1, ..., cur, cur+1, last (that's 4 numbers + ellipsis)
	return [1, cur - 2 === 2 ? 2 : 'ellipsis', cur - 1, cur, cur + 1, cur + 2 === totalPages - 1 ? totalPages - 1 : 'ellipsis', totalPages];
};

export const HotelsPagination: React.FC<HotelsPaginationProps> = ({ totalPages, currentPage, onPageClick, className }) => {
	const t = useTranslations('Keywords')
	if (totalPages <= 1) return null;

	const pages = buildPages(totalPages, currentPage);

	const goPrev = () => onPageClick(clamp(currentPage - 1, 1, totalPages));
	const goNext = () => onPageClick(clamp(currentPage + 1, 1, totalPages));

	return (
		<div className='w-full flex flex-col justify-center items-center overflow-hidden'>
			<nav className={`hotels-pagination ${className ?? ''}`} aria-label="Pagination">
				<button
					type="button"
					className="hotels-pagination__arrow"
					aria-label="Previous page"
					disabled={currentPage === 1}
					onClick={goPrev}
				>
					<Icons.chevron className="rotate-180 w-5 h-5" />
				</button>

				<ul className="hotels-pagination__list">
					{pages.map((item, idx) =>
						item === 'ellipsis' ? (
							<li key={`e-${idx}`} className="hotels-pagination__ellipsis" aria-hidden>
								…
							</li>
						) : (
							<li key={item}>
								<button
									type="button"
									className={`hotels-pagination__item ${item === currentPage ? 'hotels-pagination__item_active' : ''}`}
									aria-current={item === currentPage ? 'page' : undefined}
									onClick={() => onPageClick(item)}
								>
									{item}
								</button>
							</li>
						)
					)}
				</ul>

				<button
					type="button"
					className="hotels-pagination__arrow"
					aria-label="Next page"
					disabled={currentPage === totalPages}
					onClick={goNext}
				>
					<Icons.chevron className="w-5 h-5" />
				</button>
			</nav>
		</div>

	);
};

export default HotelsPagination;